import React from 'react';
import { Modal, Button, Message, Icon, CheckboxLabel, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetter } from '../../../../attendee/detail/components/utils';
import Styles from '../../../../../styles/form/modalAddFields.module.css';
import FormEntryModel from '../../../../../models/form_entry';
import PropTypes from 'prop-types';
import useAppContext from '../../../../../context/useAppContext';
import { usePermissions } from '../../../../../user/permissions';
import { useSegment } from '../../../../../components/tracking/trackingProvider';

export default function ModalStandardFields({
    formEntriesStandard,
    defaultForm = {},
    onChangeFormEntries = () => {},
    showModal = false,
    standardFields = [],
    onAddStandardFields = () => {},
    onClose = () => {},
    usage = null,
    switchModal = () => {}
}) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const permissions = usePermissions();
    const [warningGoogleAutoComplete, setWarningGoogleAutoComplete] = React.useState(false);
    const toast = React.useContext(ToastContext);
    const [{ feature_flags }] = useAppContext();

    const templateFormEntry = {
        id: null,
        usage: null,
        weez_field_id: null,
        required: false,
        order: null,
        show_only_bo: false,
        allow_modification: false,
        default_value: null,
        form_field: {}
    };

    const filterAndSort = React.useCallback(
        (standardFields, category) => {
            const fieldsNotDisplayable = ['ws-44', 'ws-45'];
            if (!permissions.capacities.has('PyvarCapacity.ADMIN')) fieldsNotDisplayable.push('ws-49');
            return standardFields
                .filter(field => field.field_category === category && !fieldsNotDisplayable.includes(field.weez_id))
                .sort((field_1, field_2) => field_1.order - field_2.order);
        },
        [permissions]
    );

    const standardFieldsSorted = React.useMemo(() => {
        return {
            contact: filterAndSort(standardFields, 'contact'),
            company: filterAndSort(standardFields, 'company'),
            other: filterAndSort(standardFields, 'other'),
            gdpr: filterAndSort(standardFields, 'gdpr'),
            running: filterAndSort(standardFields, 'running')
        };
    }, [standardFields]);

    const addFormEntryStandard = React.useCallback(
        (checked, value) => {
            let tmpFormEntries = [...formEntriesStandard];
            if (value === 'ws-49') setWarningGoogleAutoComplete(checked);
            if (!checked) {
                tmpFormEntries = tmpFormEntries.filter(formEntry => formEntry.form_field.weez_id !== value);
            } else {
                const tmpFormEntry = FormEntryModel.fromJSON({ ...templateFormEntry, form_field: standardFields.find(field => field.weez_id === value).copy() });
                tmpFormEntry.usage = usage;
                tmpFormEntries.push(tmpFormEntry);
            }
            onChangeFormEntries(tmpFormEntries);
        },
        [onChangeFormEntries, formEntriesStandard, usage, templateFormEntry]
    );

    const handleSubmit = React.useCallback(() => {
        onAddStandardFields(formEntriesStandard);
        toast.info(t('ticket.form.modal.standardFields.success'));
    }, [onAddStandardFields, formEntriesStandard]);

    return (
        <Modal
            className={Styles['modal-standard-fields']}
            size="large"
            open={showModal}
            onClose={() => {
                onClose();
                track('ACTION > EXIT PREDEFINED FIELD', { 'Page Title': 'CREATE FORM', 'Target type': capitalizeFirstLetter(usage) });
            }}
            title={usage === 'buyer' ? t('ticket.form.modal.standardFields.titleBuyer') : t('ticket.form.modal.standardFields.titleAttendee')}
            allowExternalClosing={false}
            submitButton={
                <Button
                    primary
                    onClick={() => {
                        handleSubmit();
                        track('ACTION > VALIDATE ADD PREDEFINED FIELD', {
                            'Page Title': 'CREATE FORM',
                            'Target type': capitalizeFirstLetter(usage),
                            'Misc field': formEntriesStandard.filter(f => f.form_field.field_category === 'other').map(f => f.form_field.label),
                            'Company field': formEntriesStandard.filter(f => f.form_field.field_category === 'company').map(f => f.form_field.label),
                            'Contact field': formEntriesStandard.filter(f => f.form_field.field_category === 'contact').map(f => f.form_field.label)
                        });
                    }}
                    label={t('ticket.common.action.add')}
                />
            }
            cancelButton={
                <Button
                    onClick={() => {
                        onClose();
                        track('ACTION > RETURN', { 'Page Title': 'CREATE FORM', 'Target type': capitalizeFirstLetter(usage), Step: 'Predefined' });
                    }}
                    label={t(`ticket.common.action.cancel`)}
                />
            }
            subtitle={
                <>
                    <Message className={Styles['message-info']} type="default" mini title={t('ticket.form.modal.standardFields.infosTitle')}>
                        <span
                            className={Styles['message-info__clickable']}
                            onClick={() => {
                                switchModal();
                                track('ACTION > CREATE CUSTOM FIELD', { 'Page Title': 'CREATE FORM', 'Target type': capitalizeFirstLetter(usage) });
                            }}
                        >
                            {t('ticket.form.modal.standardFields.infosLink')}
                            <Icon name={'chevron-right'} className={Styles['message-info__icon']} />
                        </span>
                    </Message>
                    <Message className={Styles['message-info']} type="tips" mini title={t('ticket.common.tips')}>
                        {t('ticket.form.modal.standardFields.tips')}
                    </Message>
                    {warningGoogleAutoComplete && (
                        <Message className={Styles['message-info']} type="warning" mini title={t('ticket.form.modal.standardFields.availabilityWarning.title')}>
                            <span>{t('ticket.form.modal.standardFields.availabilityWarning.subtitle')}</span>
                        </Message>
                    )}
                </>
            }
        >
            <div className={Styles['container-fields']}>
                <div className={Styles['group-fields__title']}>{t('ticket.form.modal.standardFields.categoryContact')}</div>
                <div className={Styles['grid-container-contacts']}>
                    {standardFieldsSorted.contact.map((value, index) => (
                        <CheckboxLabel
                            key={index}
                            name={value.weez_id}
                            label={value.translated_label}
                            value={value.weez_id}
                            onChange={(e, { checked, value }) => addFormEntryStandard(checked, value)}
                            checked={formEntriesStandard.some(formEntry => formEntry.form_field.weez_id === value.weez_id)}
                            disabled={defaultForm.form_entries.some(
                                formEntry => formEntry.usage === usage && formEntry.form_field.weez_id === value.weez_id && !formEntry.deletable
                            )}
                        />
                    ))}
                </div>
                <div className={Styles['grid-container-company-others']}>
                    <div>
                        <div className={Styles['group-fields__title']}>{t('ticket.form.modal.standardFields.categoryCompany')}</div>
                        <div className={Styles['grid-container-company']}>
                            {standardFieldsSorted.company.map((value, index) => (
                                <CheckboxLabel
                                    key={index}
                                    name={value.weez_id}
                                    label={value.translated_label}
                                    value={value.weez_id}
                                    onChange={(e, { checked, value }) => addFormEntryStandard(checked, value)}
                                    checked={formEntriesStandard.some(formEntry => formEntry.form_field.weez_id === value.weez_id)}
                                />
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className={Styles['group-fields__title']}>{t('ticket.form.modal.standardFields.categoryOther')}</div>
                        <div className={Styles['grid-container-others']}>
                            {standardFieldsSorted.other.map((value, index) => (
                                <CheckboxLabel
                                    key={index}
                                    name={value.weez_id}
                                    label={value.translated_label}
                                    value={value.weez_id}
                                    onChange={(e, { checked, value }) => addFormEntryStandard(checked, value)}
                                    checked={formEntriesStandard.some(formEntry => formEntry.form_field.weez_id === value.weez_id)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                {usage === 'buyer' && (
                    <div>
                        <div className={Styles['group-fields__title']}>{t('ticket.form.modal.standardFields.categoryGdpr')}</div>
                        <div className={Styles['grid-container-gdpr']}>
                            {standardFieldsSorted.gdpr.map((value, index) => (
                                <CheckboxLabel
                                    key={index}
                                    name={value.weez_id}
                                    label={value.translated_label}
                                    value={value.weez_id}
                                    onChange={(e, { checked, value }) => addFormEntryStandard(checked, value)}
                                    checked={formEntriesStandard.some(formEntry => formEntry.form_field.weez_id === value.weez_id)}
                                />
                            ))}
                        </div>
                    </div>
                )}
                {usage === 'attendee' && feature_flags.some(feature => ['EO_FOOTRACE', 'EO_DOSSARD'].includes(feature)) && (
                    <div>
                        <div className={Styles['group-fields__title']}>{t('ticket.form.modal.standardFields.categoryRunning')}</div>
                        <div className={Styles['grid-container-running']}>
                            {standardFieldsSorted.running.map((value, index) => {
                                return (
                                    ((feature_flags.includes('EO_FOOTRACE') && value.weez_id === 'ws-47') ||
                                        (feature_flags.includes('EO_DOSSARD') && value.weez_id === 'ws-48')) && (
                                        <CheckboxLabel
                                            key={index}
                                            name={value.weez_id}
                                            label={value.translated_label}
                                            value={value.weez_id}
                                            onChange={(e, { checked, value }) => addFormEntryStandard(checked, value)}
                                            checked={formEntriesStandard.some(formEntry => formEntry.form_field.weez_id === value.weez_id)}
                                        />
                                    )
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}

ModalStandardFields.propTypes = {
    formEntriesStandard: PropTypes.array.isRequired,
    standardFields: PropTypes.array,
    defaultForm: PropTypes.object,
    showModal: PropTypes.bool,
    onToggleModal: PropTypes.func,
    switchModal: PropTypes.func,
    onAddStandardFields: PropTypes.func,
    usage: PropTypes.string,
    onClose: PropTypes.func
};
